import type { IntegerAmount } from '@actual-app/core/shared/util';

import { faltante } from './objetivos';
import type { DatosCategoriaMes, ImporteCategoria } from './objetivos';

/**
 * «Asignar lo que falta» de «Asignar el mes» (B4 · H39-H41), en funciones
 * puras: cuánto falta por objetivos y por gasto de más, el reparto de lo
 * disponible y si una opción de auto-asignar cabe en lo listo para asignar.
 */

export type FilaFaltante = { id: string; datos: DatosCategoriaMes };

/**
 * Lo que necesita una categoría, partido en dos sin contar nada dos veces:
 * `objetivo` = lo que falta para el objetivo; `gastado` = lo que hace falta
 * además para que el disponible no quede en negativo. La suma es
 * `max(faltante, −saldo)`: con eso se cumplen el objetivo y el saldo ≥ 0.
 */
export function necesidadCategoria(
  datos: DatosCategoriaMes,
  incluirGastado = true,
): { objetivo: IntegerAmount; gastado: IntegerAmount } {
  const objetivo = faltante(datos);
  const gastado = incluirGastado ? Math.max(0, -datos.balance - objetivo) : 0;
  return { objetivo, gastado };
}

/** Desglose de la píldora «Faltan X · Asignar»: objetivos + gastado de más. */
export function desgloseFaltante(datos: Iterable<DatosCategoriaMes>): {
  objetivos: IntegerAmount;
  gastado: IntegerAmount;
  total: IntegerAmount;
} {
  let objetivos = 0;
  let gastado = 0;
  for (const d of datos) {
    const n = necesidadCategoria(d, true);
    objetivos += n.objetivo;
    gastado += n.gastado;
  }
  return { objetivos, gastado, total: objetivos + gastado };
}

export type PlanAsignar = {
  /** Nuevo importe asignado de cada categoría que cambia. */
  amounts: ImporteCategoria[];
  /** Lo que se asigna en total (≤ disponible). */
  total: IntegerAmount;
  /** Lo que haría falta sin límite de disponible. */
  necesario: IntegerAmount;
  /** `true` si lo disponible no llega para todo. */
  limitado: boolean;
};

/**
 * Reparte lo disponible de arriba abajo dando a cada categoría lo que
 * necesita (objetivo y, si `incluirGastado`, el gasto de más) hasta agotarlo.
 */
export function planAsignarFaltante(
  filas: readonly FilaFaltante[],
  disponible: IntegerAmount,
  incluirGastado: boolean,
): PlanAsignar {
  const amounts: ImporteCategoria[] = [];
  let restante = Math.max(0, disponible);
  let necesario = 0;
  let total = 0;
  for (const { id, datos } of filas) {
    const n = necesidadCategoria(datos, incluirGastado);
    const falta = n.objetivo + n.gastado;
    if (falta <= 0) {
      continue;
    }
    necesario += falta;
    const dado = Math.min(falta, restante);
    if (dado <= 0) {
      continue;
    }
    restante -= dado;
    total += dado;
    amounts.push({ category: id, amount: datos.budgeted + dado });
  }
  return { amounts, total, necesario, limitado: total < necesario };
}

/**
 * Importe que deja la fila con su objetivo cumplido (píldora «faltan X» de
 * la fila, H41), o `null` si no le falta nada.
 */
export function importeConObjetivo(
  datos: DatosCategoriaMes,
): IntegerAmount | null {
  const falta = faltante(datos);
  return falta > 0 ? datos.budgeted + falta : null;
}

/**
 * Cuánto sale de «Listo para asignar» al aplicar una propuesta: la suma de
 * lo que sube cada categoría menos lo que baja.
 */
export function incrementoPropuesta(
  propuesta: readonly ImporteCategoria[],
  asignadoActual: ReadonlyMap<string, IntegerAmount>,
): IntegerAmount {
  return propuesta.reduce(
    (s, a) => s + a.amount - (asignadoActual.get(a.category) ?? 0),
    0,
  );
}

/**
 * ¿Se deshabilita la opción? Sin propuesta (cargando o vacía) o si pide más
 * de lo que hay listo para asignar.
 */
export function opcionDeshabilitada(
  propuesta: readonly ImporteCategoria[] | null,
  asignadoActual: ReadonlyMap<string, IntegerAmount>,
  disponible: IntegerAmount,
): boolean {
  if (!propuesta || propuesta.length === 0) {
    return true;
  }
  const incremento = incrementoPropuesta(propuesta, asignadoActual);
  return incremento > 0 && incremento > Math.max(0, disponible);
}
